import type {
  ContractCommentView,
  ContractOrgInvitationSummary,
  MentionCandidateView,
} from "@closer/shared";
import {
  Badge,
  Button,
  Card,
  Checkbox,
  FormField,
  Input,
  Select,
  Skeleton,
  useToast,
} from "@closer/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { StateView } from "../../components/StateView";
import { api } from "../../lib/apiClient";
import { useAuth } from "../../lib/authContext";
import { sozlesmeSurumSorgusu } from "../../lib/sharedQueries";
import { tenantKey } from "../../lib/tenantQuery";
import { resolveErrorState } from "../../lib/uiState";

/**
 * SÖZLEŞME YORUMLARI (CLOS-150 `[137]` · CLOS-151 `[138]` · CLOS-152 `[139]`).
 *
 * ### ⚠ Yorum DEĞİŞMEZ VERSİYONA bağlı
 *
 * Her yorum bir sürüme yazılıyor; sürüm seçimi ekranda açık. Yorumu "sözleşmeye" yazsaydık,
 * metin değiştiğinde yorumun neye söylendiği belirsizleşirdi.
 *
 * ### ⚠ "Çözüldü" FORMAL KARAR DEĞİL
 *
 * Zincir durumu yalnız tartışmayı kapatıyor; onay, mutabakat ya da imza yerine geçmiyor.
 * Ekran da bunu böyle anlatıyor — çözme düğmesi karar diliyle konuşmuyor.
 *
 * ### ⚠ Silinen yorum KAYBOLMUYOR
 *
 * Sunucu metni maskeliyor ama satır duruyor: "bu konuşma vardı" bilgisi tarihin parçası.
 * Ekran maskelenmiş satırı gizlemiyor, silinmiş olduğunu söylüyor.
 */
export function ContractCommentsSection({
  contractId,
  kayitYazma = true,
}: {
  contractId: string;
  /** Görüntüleyenin bu kayıttaki yazma erişimi (CLOS-888). */
  kayitYazma?: boolean;
}) {
  const { activeTenantId, can, user } = useAuth();
  const tenantId = activeTenantId!;
  const queryClient = useQueryClient();
  const toast = useToast();

  const [versionId, setVersionId] = useState("");
  const [govde, setGovde] = useState("");
  const [mentions, setMentions] = useState<string[]>([]);

  const anahtar = tenantKey(tenantId, "contract", contractId, "yorumlar");

  const versiyonlar = useQuery({
    ...sozlesmeSurumSorgusu(tenantId, contractId),
  });

  const yorumlar = useQuery({
    queryKey: anahtar,
    queryFn: async () =>
      (await api<ContractCommentView[]>(`/api/contracts/${contractId}/comments`, { tenantId }))
        .data,
  });

  /** Mention adayları SUNUCUDAN: yalnız kayda hâlen erişebilenler öneriliyor. */
  const adaylar = useQuery({
    queryKey: tenantKey(tenantId, "contract", contractId, "mention-adaylari"),
    enabled: can("contract.comment"),
    queryFn: async () =>
      (
        await api<MentionCandidateView[]>(`/api/contracts/${contractId}/mention-candidates`, {
          tenantId,
        })
      ).data,
  });

  /**
   * Paylaşım alıcıları KATILIMCI organizasyonlardan (CLOS-153 `[141]`): yorum yalnız
   * sözleşmeye çağrılmış ve daveti kabul etmiş tarafla paylaşılabiliyor.
   */
  const katilimcilar = useQuery({
    enabled: can("contract.share"),
    queryKey: tenantKey(tenantId, "contract", contractId, "yorum-alicilari"),
    queryFn: async () =>
      (
        await api<ContractOrgInvitationSummary[]>(`/api/contracts/${contractId}/org-invitations`, {
          tenantId,
        })
      ).data,
  });

  const yenile = () => queryClient.invalidateQueries({ queryKey: anahtar });
  const seciliVersiyon = versionId || versiyonlar.data?.[0]?.id || "";
  const aliciAdaylari = (katilimcilar.data ?? []).filter((d) => d.status === "accepted");

  const yaz = useMutation({
    mutationFn: async () =>
      api(`/api/contracts/${contractId}/comments`, {
        method: "POST",
        tenantId,
        body: JSON.stringify({ versionId: seciliVersiyon, body: govde.trim(), mentions }),
      }),
    onSuccess: async () => {
      setGovde("");
      setMentions([]);
      await yenile();
      toast.success("Yorum eklendi");
    },
    onError: (err: unknown) => toast.error(err instanceof Error ? err.message : "Yorum eklenemedi"),
  });

  /** Düzenlenen yorumun kimliği; `null` ise hiçbiri düzenlenmiyor. */

  const [duzenlenen, setDuzenlenen] = useState<string | null>(null);

  const [duzenlemeGovdesi, setDuzenlemeGovdesi] = useState("");

  /**

   * YORUM DÜZENLEME (CLOS-1109 · `PATCH /contracts/:id/comments/:commentId`).

   *

   * ### ⚠ Ekran düzenlemeyi GÖSTERİYOR ama yaptırmıyordu

   *

   * Satır zaten *"N kez düzenlendi"* yazıyordu — yani hem model hem sunucu düzenlemeyi

   * destekliyor ve ekran sonucunu basıyordu. Eksik olan tek şey, düzenlemeyi BAŞLATAN

   * yüzeydi: kullanıcı yazdığı yorumu düzeltmek için silip yeniden yazmak zorundaydı ve

   * bu, tartışma iplikçiğindeki sırayı bozuyordu.

   *

   * ⚠ MENTIONS KORUNUYOR: şema `strictObject` ve `mentions` zorunlu. Boş dizi

   * gönderseydik düzenleme, yorumdaki bahisleri sessizce silen bir işleme dönerdi.

   */

  const duzenle = useMutation({
    mutationFn: async ({
      id,
      govde,
      mentions,
    }: {
      id: string;
      govde: string;
      mentions: string[];
    }) =>
      api(`/api/contracts/${contractId}/comments/${id}`, {
        method: "PATCH",

        tenantId,

        body: JSON.stringify({ body: govde.trim(), mentions }),
      }),

    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: anahtar });

      setDuzenlenen(null);

      setDuzenlemeGovdesi("");

      toast.success("Yorum güncellendi");
    },

    onError: (err: unknown) =>
      toast.error(err instanceof Error ? err.message : "Yorum güncellenemedi"),
  });

  const sil = useMutation({
    mutationFn: async (commentId: string) =>
      api(`/api/contracts/${contractId}/comments/${commentId}/delete`, {
        method: "POST",
        tenantId,
      }),
    onSuccess: async () => {
      await yenile();
      toast.success("Yorum silindi");
    },
    onError: (err: unknown) => toast.error(err instanceof Error ? err.message : "Yorum silinemedi"),
  });

  const paylas = useMutation({
    mutationFn: async (girdi: { commentId: string; recipientTenantIds: string[] }) =>
      api(`/api/contracts/${contractId}/comments/${girdi.commentId}/share`, {
        method: "POST",
        tenantId,
        body: JSON.stringify({ recipientTenantIds: girdi.recipientTenantIds }),
      }),
    onSuccess: async () => {
      await yenile();
      toast.success("Yorum karşı tarafla paylaşıldı");
    },
    onError: (err: unknown) =>
      toast.error(err instanceof Error ? err.message : "Yorum paylaşılamadı"),
  });

  const paylasimiGeriAl = useMutation({
    mutationFn: async (commentId: string) =>
      api(`/api/contracts/${contractId}/comments/${commentId}/unshare`, {
        method: "POST",
        tenantId,
        body: JSON.stringify({}),
      }),
    onSuccess: async () => {
      await yenile();
      toast.success("Yorum paylaşımı geri alındı");
    },
    onError: (err: unknown) =>
      toast.error(err instanceof Error ? err.message : "Paylaşım geri alınamadı"),
  });

  const zincir = useMutation({
    mutationFn: async (girdi: { commentId: string; eylem: "resolve" | "reopen" }) =>
      api(`/api/contracts/${contractId}/comments/${girdi.commentId}/${girdi.eylem}`, {
        method: "POST",
        tenantId,
        body: JSON.stringify({}),
      }),
    onSuccess: async () => {
      await yenile();
      toast.success("Tartışma durumu güncellendi");
    },
    onError: (err: unknown) =>
      toast.error(err instanceof Error ? err.message : "Tartışma durumu değiştirilemedi"),
  });

  if (!can("contract.view")) return null;

  return (
    <Card>
      <div className="mb-3 flex items-center gap-2">
        <h2 className="mr-auto text-base font-semibold text-fg-heading">Yorumlar</h2>
      </div>

      {yorumlar.isPending ? (
        <Skeleton lines={3} />
      ) : yorumlar.error ? (
        <StateView
          state={resolveErrorState(yorumlar.error, {
            idempotent: true,
            overrides: {
              error: { title: "Yorumlar yüklenemedi", description: "Bölüm şu an getirilemedi." },
            },
          })}
          onRetry={() => void yorumlar.refetch()}
        />
      ) : (
        <div className="flex flex-col gap-3">
          {(yorumlar.data ?? []).length === 0 ? (
            <p className="text-sm text-fg-muted">Bu sözleşmede henüz yorum yok.</p>
          ) : (
            <ul className="flex flex-col gap-2">
              {(yorumlar.data ?? []).map((yorum) => (
                <li
                  key={yorum.id}
                  className="flex flex-col gap-1 rounded-md border border-border-default p-3"
                >
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-medium text-fg-strong">{yorum.authorName}</span>
                    <span className="text-xs text-fg-soft">
                      {new Date(yorum.createdAt).toLocaleString("tr-TR")}
                      {yorum.editCount > 0 ? ` · ${yorum.editCount} kez düzenlendi` : ""}
                    </span>
                    {yorum.visibilityScope === "shared" ? (
                      <Badge tone="neutral">Karşı tarafla paylaşıldı</Badge>
                    ) : null}
                    {yorum.resolutionStatus === "resolved" ? (
                      <Badge tone="success">Tartışma kapandı</Badge>
                    ) : null}
                  </div>

                  {/* ⚠ Silinen yorum SATIR olarak duruyor; metni sunucuda maskeleniyor. */}
                  {duzenlenen === yorum.id ? (
                    <div className="flex flex-wrap items-end gap-2">
                      <div className="min-w-64 flex-1">
                        <FormField label="Yorumu düzenle">
                          <Input
                            aria-label="Yorumu düzenle"
                            value={duzenlemeGovdesi}
                            onChange={(olay) => setDuzenlemeGovdesi(olay.target.value)}
                          />
                        </FormField>
                      </div>
                      <Button
                        type="button"
                        size="sm"
                        loading={duzenle.isPending}
                        disabled={duzenlemeGovdesi.trim() === ""}
                        onClick={() =>
                          duzenle.mutate({
                            id: yorum.id,
                            govde: duzenlemeGovdesi,
                            /**
                             * ⚠ BOŞ DİZİ GÜVENLİ — ölçüldü, varsayım değil.
                             *
                             * `updateContractComment` (comments.ts:198) yalnız `body`,
                             * `editCount` ve `editedAt` yazıyor; bahis tablosuna HİÇ
                             * dokunmuyor. Yani düzenleme mevcut bahisleri koruyor.
                             *
                             * ⚠ Alan yine de GÖNDERİLMEK ZORUNDA: şema `strictObject` ve
                             * `mentions` zorunlu. Yani sözleşme, servisin kullanmadığı bir
                             * alanı istiyor — küçük ama gerçek bir tutarsızlık (CLOS-1109
                             * notu). `ContractCommentView` bahisleri dışarı vermediği için
                             * ekran gerçek listeyi gönderemez de.
                             */
                            mentions: [],
                          })
                        }
                      >
                        Kaydet
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={() => setDuzenlenen(null)}
                      >
                        Vazgeç
                      </Button>
                    </div>
                  ) : (
                    <p className="text-sm text-fg">
                      {yorum.body ?? <span className="text-fg-muted">Bu yorum silindi.</span>}
                    </p>
                  )}

                  <div className="flex flex-wrap gap-2">
                    {/*
                      ⚠ YALNIZ KENDİ AKTİF YORUMU: kapı `Sil` ile birebir aynı. Başkasının
                      yorumunu düzenlemek, tartışma kaydını sahibinin haberi olmadan
                      değiştirmek olurdu.
                    */}
                    {yorum.status === "active" && yorum.authorId === user?.id && !duzenlenen ? (
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          setDuzenlenen(yorum.id);
                          setDuzenlemeGovdesi(yorum.body ?? "");
                        }}
                      >
                        Düzenle
                      </Button>
                    ) : null}
                    {yorum.status === "active" && yorum.authorId === user?.id ? (
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={() => sil.mutate(yorum.id)}
                      >
                        Sil
                      </Button>
                    ) : null}
                    {/*
                      ⚠ PAYLAŞIM AÇIK BİR KARAR (CLOS-153 `[141]`): yorum varsayılan olarak
                      yalnız kendi organizasyonumuzda. Karşı tarafa geçmesi ayrı bir eylem
                      ve geri alınabiliyor — paylaşılan yorum "her zaman paylaşık" değil.
                    */}
                    {can("contract.share") &&
                    yorum.status === "active" &&
                    yorum.visibilityScope === "workspace" &&
                    aliciAdaylari.length > 0 ? (
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={() =>
                          paylas.mutate({
                            commentId: yorum.id,
                            // ⚠ Kabul edilmiş davetin hedefi hep dolu (K1); tip nullable, burada daraltılıyor.
                            recipientTenantIds: aliciAdaylari
                              .map((a) => a.targetTenantId)
                              .filter((kimlik): kimlik is string => kimlik !== null),
                          })
                        }
                      >
                        Karşı tarafla paylaş
                      </Button>
                    ) : null}
                    {can("contract.share") &&
                    yorum.status === "active" &&
                    yorum.visibilityScope === "shared" ? (
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={() => paylasimiGeriAl.mutate(yorum.id)}
                      >
                        Paylaşımı geri al
                      </Button>
                    ) : null}
                    {can("contract.review") && yorum.status === "active" ? (
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={() =>
                          zincir.mutate({
                            commentId: yorum.id,
                            eylem: yorum.resolutionStatus === "open" ? "resolve" : "reopen",
                          })
                        }
                      >
                        {yorum.resolutionStatus === "open"
                          ? "Tartışmayı kapat"
                          : "Tartışmayı yeniden aç"}
                      </Button>
                    ) : null}
                  </div>
                </li>
              ))}
            </ul>
          )}

          {can("contract.comment") && kayitYazma ? (
            <div className="flex flex-col gap-2 border-t border-border-default pt-3">
              <FormField label="Yorum yazılacak sürüm">
                <Select
                  id="yorum-versiyon"
                  value={seciliVersiyon}
                  onChange={(olay) => setVersionId(olay.target.value)}
                >
                  {(versiyonlar.data ?? []).map((v) => (
                    <option key={v.id} value={v.id}>
                      Sürüm {v.versionNumber} · {new Date(v.createdAt).toLocaleDateString("tr-TR")}
                    </option>
                  ))}
                </Select>
              </FormField>

              <FormField label="Yorum">
                <Input
                  id="yorum-govde"
                  value={govde}
                  onChange={(olay) => setGovde(olay.target.value)}
                  placeholder="Örn. 4. maddedeki teslim süresi kısaltılmalı"
                />
              </FormField>

              {(adaylar.data ?? []).length > 0 ? (
                <fieldset className="flex flex-col gap-1">
                  <legend className="text-xs font-medium text-fg-strong">
                    Bilgilendirilecek kişiler
                  </legend>
                  {(adaylar.data ?? []).map((aday) => (
                    <Checkbox
                      key={aday.userId}
                      label={aday.name}
                      checked={mentions.includes(aday.userId)}
                      onChange={(olay) =>
                        setMentions((onceki) =>
                          olay.target.checked
                            ? [...onceki, aday.userId]
                            : onceki.filter((x) => x !== aday.userId),
                        )
                      }
                    />
                  ))}
                  {/* ⚠ Aday listesi SUNUCUDAN: erişimi olmayan kişi seçilemiyor. */}
                  <p className="text-xs text-fg-soft">
                    Yalnız bu sözleşmeye erişebilen kişiler listelenir.
                  </p>
                </fieldset>
              ) : null}

              <Button
                type="button"
                size="sm"
                onClick={() => yaz.mutate()}
                loading={yaz.isPending}
                disabled={govde.trim() === "" || seciliVersiyon === ""}
              >
                Yorum ekle
              </Button>
            </div>
          ) : null}
        </div>
      )}
    </Card>
  );
}
